import type { MatchdayStatus } from '@/types/api'

/** Matchday lifecycle, collapsed to the three states the interface shows. */
export type MatchdayState = 'pending' | 'locked' | 'processed'

export function matchdayState(status: MatchdayStatus | string | undefined): MatchdayState {
    const value = (status || '').toUpperCase()
    if (value === 'PROCESSED') return 'processed'
    if (value === 'CLOSED' || value === 'PROCESSING') return 'locked'
    return 'pending'
}

export const stateLabel: Record<MatchdayState, string> = {
    pending: 'Abierta',
    locked: 'Cerrada',
    processed: 'Puntuada'
}

/** Points a single prediction earned, as a quality tier. */
export type ResultTier = 'exact' | 'signdiff' | 'signonly' | 'miss'

export function pointsTier(points: number | null | undefined): ResultTier {
    const value = points ?? 0
    if (value >= 12) return 'exact'
    if (value >= 7) return 'signdiff'
    if (value >= 5) return 'signonly'
    return 'miss'
}

const dateFormatter = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short'
})

const dateTimeFormatter = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
})

export function formatDate(value: string | Date | null | undefined): string {
    if (!value) return '—'
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date)
}

export function formatDateTime(value: string | Date | null | undefined): string {
    if (!value) return '—'
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date)
}

/** "faltan 2 d 4 h" / "cerrada" — the deadline countdown, coarse on purpose. */
export function timeUntil(value: string | Date | null | undefined): string {
    if (!value) return ''
    const date = value instanceof Date ? value : new Date(value)
    const diff = date.getTime() - Date.now()
    if (Number.isNaN(diff)) return ''
    if (diff <= 0) return 'cerrada'

    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `faltan ${days} d ${hours % 24} h`
    if (hours > 0) return `faltan ${hours} h ${minutes % 60} min`
    return `faltan ${minutes} min`
}

/** The player's display name, never blank. */
export function displayName(user: { name?: string | null; email?: string } | null | undefined): string {
    if (!user) return 'Jugador'
    return user.name?.trim() || user.email?.split('@')[0] || 'Jugador'
}

export function initialsOf(value: string | null | undefined): string {
    const name = (value || '').trim()
    if (!name) return '?'
    return name
        .split(/\s+/)
        .map((part) => part[0] ?? '')
        .join('')
        .toUpperCase()
        .slice(0, 2)
}

/** Signed number for a trend column: +3, −2, 0. */
export function signed(value: number | null | undefined): string {
    const number = value ?? 0
    if (number > 0) return `+${number}`
    if (number < 0) return `−${Math.abs(number)}`
    return '0'
}

/**
 * Club names a 390px row can hold. Only the handful that truncate get an
 * entry; everything else displays as stored. Data-driven display, not a
 * rename — the full name stays everywhere space allows.
 */
const SHORT_TEAM_NAMES: Record<string, string> = {
    'Atlético Madrid': 'Atlético',
    'Real Sociedad': 'R. Sociedad',
    'Real Madrid': 'R. Madrid',
    'Real Betis': 'Betis'
}

export function shortTeamName(name: string): string {
    return SHORT_TEAM_NAMES[name] ?? name
}
